import type { LearnTopic } from "../topic-types";

/**
 * Grounding techniques — Learn Hub topic page.
 *
 * Educational, non-diagnostic hub for grounding: what it is, which kind
 * to reach for, and a set of practices to try.
 */
export const GROUNDING_TECHNIQUES_TOPIC: LearnTopic = {
  slug: "grounding-techniques",
  emoji: "🌱",
  title: "Grounding Techniques",
  tagline: "Ways to come back to the present when the body has left it",
  theme: "sage",
  category: "nervous-system",
  isNew: true,
  seoDescription:
    "A trauma-informed guide to grounding techniques — sensory, physical, mental and soothing grounding, including the 5-4-3-2-1 method — with guidance on which to use when. From Recovery Trauma™.",
  cardBlurb:
    "Sensory, physical, mental and soothing grounding — and how to choose the one that fits the moment.",
  intro: [
    "Grounding is anything that helps you reconnect with the present moment when your system has moved into overwhelm, flashback, numbness or shutdown. It is not a cure, and it is not a way of pushing feelings away — it is a way of steadying yourself enough to have some choice about what happens next.",
    "There is no single correct technique. Different states need different approaches: what helps when you are flooded with anxiety is often not what helps when you feel far away and disconnected.",
    "This hub gathers the main families of grounding, a way of choosing between them, and practices you can try today.",
  ],
  closing:
    "Grounding works best when practised in calm moments, so it is familiar when you need it. If overwhelm is frequent or severe, a trauma-informed therapist can help you build a plan that fits your history.",
  continueTo: [
    { label: "Guided grounding page", to: "/grounding", note: "Practise along with the platform's grounding space" },
    { label: "Hypervigilance", to: "/learn/hypervigilance" },
    { label: "Understanding your triggers", to: "/learn/triggers" },
    { label: "50 grounding techniques — full article", to: "/blog/50-grounding-techniques-for-anxiety-ptsd-cptsd" },
  ],
  sections: [
    {
      slug: "understand",
      emoji: "📚",
      title: "Understand",
      blurb: "What grounding is for, and which kind fits which state.",
      resources: [
        {
          slug: "what-is-grounding",
          kind: "learn",
          title: "What grounding actually does",
          summary: "Anchoring attention in the present, through the senses and the body.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "When the nervous system is activated or shut down, attention drifts away from the here and now — into the past, into worry, or into a foggy sense of distance. Grounding gently re-anchors attention to something present and verifiable: a texture, a sound, the floor under your feet, the temperature of the air.",
            },
            {
              type: "list",
              title: "What grounding is — and is not",
              items: [
                "It is a way of steadying, not a way of avoiding feelings",
                "It does not need to produce calm to have been useful",
                "It works better with practice, in easier moments first",
                "There is no failure here — a technique that does not suit you is simply information",
              ],
            },
            {
              type: "note",
              text: "Educational content only. If you are in immediate danger or crisis, please use the crisis support page or your local emergency services.",
            },
          ],
        },
        {
          slug: "choosing-a-technique",
          kind: "learn",
          title: "Choosing the right kind for the moment",
          summary: "Flooded, frozen, or far away — each needs something different.",
          minutes: 3,
          blocks: [
            {
              type: "list",
              title: "A rough guide",
              items: [
                "Flooded, racing, panicky → sensory grounding with strong input: cold water, texture, a firm surface",
                "Numb, foggy, far away → movement and orienting: standing, walking, naming what you see",
                "Spiralling in thought → mental grounding: counting backwards, categories, naming facts",
                "Raw, tender, distressed → soothing grounding: warmth, a blanket, a kind phrase, a familiar smell",
              ],
            },
            {
              type: "flow",
              title: "Simple sequence",
              items: ["Notice the state", "Pick a matching kind", "Try for 60 seconds", "Check in", "Repeat or switch"],
            },
          ],
        },
      ],
    },
    {
      slug: "practices",
      emoji: "🌿",
      title: "Practices",
      blurb: "Techniques to try, one at a time.",
      resources: [
        {
          slug: "five-four-three-two-one",
          kind: "practice",
          title: "The 5-4-3-2-1 method",
          summary: "The best-known sensory grounding technique, step by step.",
          minutes: 4,
          blocks: [
            {
              type: "steps",
              title: "Work down through the senses",
              items: [
                { label: "Name five things you can see", note: "Ordinary things count — a lamp, a crack in the paint." },
                { label: "Name four things you can feel", note: "Fabric, chair, floor, air on your skin." },
                { label: "Name three things you can hear" },
                { label: "Name two things you can smell", note: "Or two smells you like, if there are none." },
                { label: "Name one thing you can taste, or one slow breath" },
              ],
            },
            {
              type: "note",
              text: "Going slowly matters more than completing the list. Stopping partway through is fine.",
            },
          ],
        },
        {
          slug: "physical-grounding",
          kind: "practice",
          title: "Physical grounding",
          summary: "Using the body and gravity to arrive back in the room.",
          minutes: 3,
          blocks: [
            {
              type: "list",
              title: "Options to try",
              items: [
                "Press both feet firmly into the floor and notice the push back",
                "Hold something cold — a glass, a tap, an ice cube",
                "Push your palms together, hold for ten seconds, release",
                "Walk slowly, deliberately noticing each step",
                "Stretch tall, then let the shoulders drop",
              ],
            },
          ],
        },
        {
          slug: "mental-grounding",
          kind: "practice",
          title: "Mental grounding",
          summary: "Giving a spiralling mind something structured to hold.",
          minutes: 3,
          blocks: [
            {
              type: "list",
              title: "Options to try",
              items: [
                "Count backwards from 100 in sevens",
                "Name every blue object in the room",
                "List animals, cities or songs alphabetically",
                "Say the date, your location, and your age out loud",
                "Describe a familiar route in as much detail as you can",
              ],
            },
          ],
        },
        {
          slug: "soothing-grounding",
          kind: "practice",
          title: "Soothing grounding",
          summary: "Kindness as a regulation strategy, not an indulgence.",
          minutes: 3,
          blocks: [
            {
              type: "list",
              title: "Options to try",
              items: [
                "Wrap yourself in something warm and heavy",
                "Hold a warm drink and notice the heat in your hands",
                "Say one steady phrase to yourself: 'this is hard, and it will pass'",
                "Bring to mind a place, animal or person that feels safe",
                "Put on a familiar piece of music you know well",
              ],
            },
            { type: "cta", text: "Practise along with the guided grounding space", label: "Open grounding", to: "/grounding" },
          ],
        },
      ],
    },
    {
      slug: "make-it-yours",
      emoji: "✍️",
      title: "Make it yours",
      blurb: "Build a short list you can actually reach for under pressure.",
      resources: [
        {
          slug: "my-grounding-plan",
          kind: "worksheet",
          title: "My grounding plan",
          summary: "A printable card of the few techniques that work for you.",
          worksheetFields: [
            { label: "When I feel flooded, I will try", lines: 2 },
            { label: "When I feel numb or far away, I will try", lines: 2 },
            { label: "When my thoughts spiral, I will try", lines: 2 },
            { label: "Objects or items that help me", lines: 2 },
            { label: "People I can message", lines: 2 },
            { label: "What I want to remember in a hard moment", lines: 2 },
          ],
        },
        {
          slug: "reflect-on-what-works",
          kind: "reflect",
          title: "Noticing what works for you",
          summary: "Short prompts to review your own experience.",
          minutes: 5,
          blocks: [
            {
              type: "prompts",
              items: [
                "Which technique changed something, even slightly?",
                "Which one made things feel worse, and can I set it aside?",
                "Where am I most likely to need grounding this week?",
                "What would make it easier to remember in the moment?",
              ],
            },
          ],
        },
      ],
    },
  ],
};
